import { Navigate, Outlet, type RouteObject } from 'react-router';

import { InternalDashboardLayout } from '@ll-platform/frontend/components/InternalDashboard/components/layout/InternalDashboardLayout';
import { NavigationBarLayout } from '@ll-platform/frontend/components/InternalDashboard/components/layout/NavigationBarLayout';
import { InternalDashboardRedirectController } from '@ll-platform/frontend/components/InternalDashboard/pages/InternalDashboardRedirectController';
import { BaseLayout } from '@ll-platform/frontend/components/layouts/BaseLayout';
import {
  DashboardLayout,
  ExternalDashboardLayout,
} from '@ll-platform/frontend/components/layouts/DashboardLayout/DashboardLayout';
import { withLoader } from '@ll-platform/frontend/components/router/helpers';
import SuspenseLoader from '@ll-platform/frontend/components/SuspenseLoader';
import { AuthRedirectController } from '@ll-platform/frontend/core/auth/guards/AuthRedirectController';
import {
  IsExternalLoggedInGuard,
  IsInternalLoggedInGuard,
  IsLoggedInGuard,
} from '@ll-platform/frontend/core/auth/guards/IsLoggedInGuard';
import { OrganizationGuard } from '@ll-platform/frontend/core/organization/guards/OrganizationGuard';
import { Pages } from '@ll-platform/frontend/core/router/pages';
import { ActiveProjectMemberGuard } from '@ll-platform/frontend/features/projects/guards/ActiveProjectMemberGuard';
import { lazyLoad } from '@ll-platform/frontend/utils/router/lazyLoad';

const CreativeProposalFeatureFlagGuard = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/pages/CreativeProposalFeatureFlagGuard'
      ),
  ).CreativeProposalFeatureFlagGuard,
);

const RedirectInternalProjectSpace = withLoader(
  lazyLoad(
    () => import('@ll-platform/frontend/features/RedirectInternalProjectSpace'),
  ).RedirectInternalProjectSpace,
);

const EditProjectFormProvider = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/internalProjects/pages/ProjectForm/routeStepper/EditProjectFormProvider'
      ),
  ).EditProjectFormProvider,
);

const ProjectFormRouteLayout = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/internalProjects/pages/ProjectForm/routeStepper/ProjectFormRouteLayout/ProjectFormRouteLayout'
      ),
  ).ProjectFormRouteLayout,
);

const ProjectFormProvider = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/internalProjects/pages/ProjectForm/routeStepper/ProjectFormRouteProvider'
      ),
  ).ProjectFormRouteProvider,
);

const ActiveProjectBaseDataGuard = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projects/guards/ActiveProjectBaseDataGuard'
      ),
  ).ActiveProjectBaseDataGuard,
);

const ActiveProposalContainer = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/containers/ActiveProposalContainer'
      ),
  ).default,
);

const ActiveProposalProjectsContainer = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/containers/ActiveProposalProjectsContainer'
      ),
  ).default,
);

const WizardLayout = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectWizard/components/layout/WizardLayout'
      ),
  ).WizardLayout,
);

const WizardNavigationContextProvider = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectWizard/contexts/WizardNavigationContext'
      ),
  ).WizardNavigationContextProvider,
);

const ActiveProjectInternalUserGuard = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projects/guards/ActiveProjectInternalUserGuard'
      ),
  ).ActiveProjectInternalUserGuard,
);

const ActiveProjectContainer = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectWizard/containers/ActiveProjectContainer'
      ),
  ).default,
);

const ProjectsListPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projects/pages/ProjectsList/ProjectsListPage'
      ),
  ).default,
);
const InternalBrandPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/brands/pages/internal/InternalBrandPage/InternalBrandPage'
      ),
  ).default,
);

const ProjectFormController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/internalProjects/pages/ProjectForm/routeStepper/ProjectFormRouteController'
      ),
  ).ProjectFormRouteController,
);

const ProjectFormDefaultStepController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/internalProjects/pages/ProjectForm/routeStepper/ProjectFormDefaultStepRouteController'
      ),
  ).ProjectFormDefaultStepRouteController,
);

const InternalProjectListPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/internalProjects/pages/ProjectsListPage/ProjectsListPage'
      ),
  ).default,
);
const ContractsPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/contracts/pages/ContractsPage/ContractsPage'
      ),
  ).ContractsPage,
);
const NewContractPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/contracts/pages/NewContractPage/NewContractPage'
      ),
  ).NewContractPage,
);
const EditContractPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/contracts/pages/EditContractPage/EditContractPage'
      ),
  ).EditContractPage,
);
const ReviewDraftContractPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/contracts/pages/ReviewDraftContractPage/ReviewDraftContractPage'
      ),
  ).ReviewDraftContractPage,
);

const ProposalPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/pages/ProposalPage/ProposalPage'
      ),
  ).ProposalPage,
);
const ProposalDoc = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/pages/ProposalPage/ProposalDoc'
      ),
  ).ProposalDoc,
);
const NewProposalPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/pages/NewProposalPage/NewProposalPage'
      ),
  ).NewProposalPage,
);
const EditProposalPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/pages/EditProposalPage/EditProposalPage'
      ),
  ).EditProposalPage,
);

const CallSheetV2Page = withLoader(
  lazyLoad(
    () => import('@ll-platform/frontend/features/callSheet/CallSheetV2Page'),
  ).CallSheetV2Page,
);

const ProjectSpaceDefaultRouteController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectSpace/pages/ProjectSpaceDefaultRouteController'
      ),
  ).ProjectSpaceDefaultRouteController,
);

const ProjectSpaceController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectSpace/pages/ProjectSpaceController'
      ),
  ).ProjectSpaceController,
);

const AdminReportsPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/adminReports/pages/AdminReportsPage'
      ),
  ).AdminReportsPage,
);

const PlaygroundsIndex = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/components/InternalDashboard/pages/PlaygroundsIndex'
      ),
  ).PlaygroundsIndex,
);

const PromptPlayground = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/llm/pages/PromptPlayground/PromptPlayground'
      ),
  ).PromptPlayground,
);

const WardrobePlayground = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectWizard/pages/WardrobePlayground/WardrobePlayground'
      ),
  ).WardrobePlayground,
);

const ThemePlayground = withLoader(
  lazyLoad(
    () => import('@ll-platform/frontend/features/playground/ThemePlayground'),
  ).ThemePlayground,
);

const TextEditorPlayground = withLoader(
  lazyLoad(
    () =>
      import('@ll-platform/frontend/features/textEditor/TextEditorPlayground'),
  ).TextEditorPlayground,
);

const ProjectSpacePlayground = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectSpace/ProjectSpacePlayground'
      ),
  ).ProjectSpacePlayground,
);

const VideoPlayerPlayground = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/videoPlayer/VideoPlayerPlayground'
      ),
  ).VideoPlayerPlayground,
);

const CollabGridPlayground = withLoader(
  lazyLoad(
    () => import('@ll-platform/frontend/features/playground/GridPlayground'),
  ).GridPlayground,
);

const ProjectWizardController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectWizard/pages/ProjectWizardController'
      ),
  ).ProjectWizardController,
);
const ProjectWizardDefaultStepController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/projectWizard/pages/ProjectWizardDefaultStepController'
      ),
  ).ProjectWizardDefaultStepController,
);

const ProposalDefaultProjectWizardController = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/proposals/pages/ProposalDefaultProjectWizardController/ProposalDefaultProjectWizardController'
      ),
  ).ProposalDefaultProjectWizardController,
);

const MyAccountPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/users/pages/myAccount/MyAccountPage'
      ),
  ).MyAccountPage,
);

const VideoReviewPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/postProductionMedia/pages/VideoReview/VideoReviewPage'
      ),
  ).VideoReviewPage,
);

const VideoPublicPage = withLoader(
  lazyLoad(
    () =>
      import(
        '@ll-platform/frontend/features/postProductionMedia/pages/VideoPublic/VideoPublicPage'
      ),
  ).VideoPublicPage,
);

export const appRoutes: RouteObject[] = [
  {
    path: '',
    element: (
      <BaseLayout>
        <AuthRedirectController loadingComponent={<SuspenseLoader />} />
      </BaseLayout>
    ),
  },
  {
    path: 'internal',
    children: [
      { path: '', element: <Navigate to="/" replace /> },
      {
        path: 'project-space',
        element: <RedirectInternalProjectSpace />,
        children: [
          {
            path: ':id',
            element: <RedirectInternalProjectSpace />,
            children: [
              {
                path: '',
                element: <RedirectInternalProjectSpace />,
              },
              {
                path: ':page',
                element: <RedirectInternalProjectSpace />,
              },
            ],
          },
        ],
      },
      {
        path: 'register',
        element: <Navigate to="/register" replace />,
      },
      {
        path: 'login',
        element: <Navigate to="/login" replace />,
      },

      {
        path: 'brand/:id',
        element: (
          <IsInternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
            <OrganizationGuard>
              <InternalDashboardLayout>
                <Outlet />
              </InternalDashboardLayout>
            </OrganizationGuard>
          </IsInternalLoggedInGuard>
        ),
        children: [
          {
            path: '',
            element: <Navigate to="projects" relative="path" replace />,
          },
          {
            path: ':tab',
            element: <InternalBrandPage />,
          },
        ],
      },
      {
        path: 'dashboard',
        element: (
          <IsInternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
            <OrganizationGuard>
              <Outlet />
            </OrganizationGuard>
          </IsInternalLoggedInGuard>
        ),
        children: [
          { path: '', element: <InternalDashboardRedirectController /> },
          {
            path: 'projects',
            element: (
              <InternalDashboardLayout>
                <InternalProjectListPage />
              </InternalDashboardLayout>
            ),
          },
          {
            path: 'contracts',
            children: [
              {
                path: '',
                element: (
                  <InternalDashboardLayout>
                    <ContractsPage />
                  </InternalDashboardLayout>
                ),
              },
              {
                path: 'new',
                element: (
                  <NavigationBarLayout>
                    <NewContractPage />
                  </NavigationBarLayout>
                ),
              },
              {
                path: 'edit',
                children: [
                  {
                    path: ':id',
                    element: (
                      <NavigationBarLayout>
                        <EditContractPage />
                      </NavigationBarLayout>
                    ),
                  },
                ],
              },
              {
                path: 'review-draft',
                children: [
                  {
                    path: ':id',
                    element: (
                      <NavigationBarLayout>
                        <ReviewDraftContractPage />
                      </NavigationBarLayout>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'proposals',
            children: [
              {
                path: '',
                element: (
                  <CreativeProposalFeatureFlagGuard>
                    <InternalDashboardLayout>
                      <ProposalPage />
                    </InternalDashboardLayout>
                  </CreativeProposalFeatureFlagGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <CreativeProposalFeatureFlagGuard>
                    <NavigationBarLayout>
                      <NewProposalPage />
                    </NavigationBarLayout>
                  </CreativeProposalFeatureFlagGuard>
                ),
              },
              {
                path: 'edit',
                children: [
                  {
                    path: ':id',
                    element: (
                      <CreativeProposalFeatureFlagGuard>
                        <NavigationBarLayout>
                          <EditProposalPage />
                        </NavigationBarLayout>
                      </CreativeProposalFeatureFlagGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'proposal',
            children: [
              {
                path: ':proposalId',
                children: [
                  {
                    path: '',
                    element: <ProposalDefaultProjectWizardController />,
                  },
                  {
                    path: 'project',
                    element: (
                      <IsInternalLoggedInGuard
                        loadingComponent={<SuspenseLoader />}
                      >
                        <OrganizationGuard>
                          <Outlet />
                        </OrganizationGuard>
                      </IsInternalLoggedInGuard>
                    ),
                    children: [
                      {
                        path: ':id',
                        children: [
                          {
                            path: ':flowType',
                            element: (
                              <ActiveProjectContainer>
                                <WizardNavigationContextProvider>
                                  <WizardLayout>
                                    <Outlet />
                                  </WizardLayout>
                                </WizardNavigationContextProvider>
                              </ActiveProjectContainer>
                            ),
                            children: [
                              {
                                path: '',
                                element: <ProjectWizardDefaultStepController />,
                              },
                              {
                                path: ':step',
                                element: <ProjectWizardController />,
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'project',
        children: [
          {
            path: 'new',
            element: (
              <IsInternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
                <OrganizationGuard>
                  <ProjectFormProvider>
                    <ProjectFormRouteLayout>
                      <Outlet />
                    </ProjectFormRouteLayout>
                  </ProjectFormProvider>
                </OrganizationGuard>
              </IsInternalLoggedInGuard>
            ),
            children: [
              {
                path: '',
                element: <ProjectFormDefaultStepController />,
              },
              {
                path: ':step',
                element: <ProjectFormController />,
              },
            ],
          },
          {
            path: 'edit/:id',
            element: (
              <IsInternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
                <OrganizationGuard>
                  <EditProjectFormProvider>
                    <ProjectFormRouteLayout>
                      <Outlet />
                    </ProjectFormRouteLayout>
                  </EditProjectFormProvider>
                </OrganizationGuard>
              </IsInternalLoggedInGuard>
            ),
            children: [
              {
                path: '',
                element: <ProjectFormDefaultStepController />,
              },
              {
                path: ':step',
                element: <ProjectFormController />,
              },
            ],
          },
        ],
      },
      {
        path: 'admin',
        element: (
          <IsInternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
            <OrganizationGuard>
              <InternalDashboardLayout>
                <Outlet />
              </InternalDashboardLayout>
            </OrganizationGuard>
          </IsInternalLoggedInGuard>
        ),
        children: [
          {
            path: 'reports',
            element: <AdminReportsPage />,
          },
        ],
      },
      {
        path: 'playgrounds',
        element: (
          <IsInternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
            <NavigationBarLayout>
              <Outlet />
            </NavigationBarLayout>
          </IsInternalLoggedInGuard>
        ),
        children: [
          {
            path: '',
            element: <PlaygroundsIndex />,
          },
          {
            path: 'prompt',
            element: <PromptPlayground />,
          },
          {
            path: 'wardrobe',
            element: <WardrobePlayground />,
          },
          {
            path: 'theme',
            element: <ThemePlayground />,
          },
          {
            path: 'text-editor',
            element: <TextEditorPlayground />,
          },
          {
            path: 'project-space',
            element: <ProjectSpacePlayground />,
          },
          {
            path: 'video-player',
            element: <VideoPlayerPlayground />,
          },
          {
            path: 'collab-grid',
            element: <CollabGridPlayground />,
          },
        ],
      },
      {
        path: 'prompt-playground',
        element: <Navigate to={Pages.InternalPromptPlayground} replace />,
      },
      {
        path: 'theme-playground',
        element: <Navigate to={Pages.InternalThemePlayground} replace />,
      },
    ],
  },
  {
    path: 'dashboard',
    children: [
      {
        path: '',
        element: <Navigate to="projects" replace />,
      },
      {
        path: 'projects',
        element: (
          <IsExternalLoggedInGuard loadingComponent={<SuspenseLoader />}>
            <ExternalDashboardLayout>
              <ProjectsListPage />
            </ExternalDashboardLayout>
          </IsExternalLoggedInGuard>
        ),
      },
      {
        path: 'project',
        element: (
          <IsLoggedInGuard loadingComponent={<SuspenseLoader />}>
            <OrganizationGuard ignoreExternalUsers>
              <Outlet />
            </OrganizationGuard>
          </IsLoggedInGuard>
        ),
        children: [
          {
            path: ':id',
            children: [
              {
                path: ':flowType',
                element: (
                  <ActiveProjectInternalUserGuard>
                    <ActiveProjectMemberGuard>
                      <ActiveProjectContainer>
                        <WizardNavigationContextProvider>
                          <WizardLayout>
                            <Outlet />
                          </WizardLayout>
                        </WizardNavigationContextProvider>
                      </ActiveProjectContainer>
                    </ActiveProjectMemberGuard>
                  </ActiveProjectInternalUserGuard>
                ),
                children: [
                  {
                    path: '',
                    element: <ProjectWizardDefaultStepController />,
                  },
                  {
                    path: ':step',
                    element: <ProjectWizardController />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'project-space',
    children: [
      {
        path: ':id',
        element: (
          <ActiveProjectContainer>
            <Outlet />
          </ActiveProjectContainer>
        ),
        children: [
          {
            path: '',
            element: <ProjectSpaceDefaultRouteController />,
          },
          {
            // legacy route, preserve query params in redirect
            path: 'client-assets',
            element: (
              <Navigate
                to={{
                  pathname: '../assets',
                  search: location.search,
                }}
                replace
              />
            ),
          },
          {
            path: ':page/*',
            element: <ProjectSpaceController />,
          },
        ],
      },
    ],
  },
  {
    path: 'post-production',
    children: [
      {
        path: ':id',
        children: [
          {
            path: 'review',
            element: (
              <IsLoggedInGuard loadingComponent={<SuspenseLoader />}>
                <OrganizationGuard ignoreExternalUsers>
                  <ActiveProjectMemberGuard>
                    <ActiveProjectBaseDataGuard>
                      <Outlet />
                    </ActiveProjectBaseDataGuard>
                  </ActiveProjectMemberGuard>
                </OrganizationGuard>
              </IsLoggedInGuard>
            ),
            children: [
              {
                path: ':deliverableKey',
                children: [
                  {
                    path: '',
                    element: <VideoReviewPage />,
                  },
                  {
                    path: ':versionName',
                    element: <VideoReviewPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'public',
            element: (
              <ActiveProjectBaseDataGuard>
                <Outlet />
              </ActiveProjectBaseDataGuard>
            ),
            children: [
              {
                path: ':deliverableKey',
                children: [
                  {
                    path: '',
                    element: <VideoPublicPage />,
                  },
                  {
                    path: ':versionName',
                    element: <VideoPublicPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'project',
    children: [
      {
        path: ':projectId',
        children: [
          {
            path: 'call-sheet',
            children: [
              {
                path: ':callSheetId/:mode',
                element: (
                  <ActiveProjectContainer projectParamKey="projectId">
                    <CallSheetV2Page />
                  </ActiveProjectContainer>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'proposal/:proposalId',
    element: (
      <CreativeProposalFeatureFlagGuard>
        <ActiveProposalContainer>
          <ActiveProposalProjectsContainer>
            <ProposalDoc />
          </ActiveProposalProjectsContainer>
        </ActiveProposalContainer>
      </CreativeProposalFeatureFlagGuard>
    ),
  },
  {
    path: 'settings',
    element: (
      <IsLoggedInGuard loadingComponent={<SuspenseLoader />}>
        <DashboardLayout>
          <Outlet />
        </DashboardLayout>
      </IsLoggedInGuard>
    ),
    children: [
      {
        path: 'my-account',
        element: <MyAccountPage />,
      },
    ],
  },
];
